import "./style.scss";

import { Avatar, Skeleton, Tooltip } from "@mui/material";
import Beneficiaries from "assets/img/svg/navbar/beneficiaries";
import Titles from "assets/img/svg/navbar/titles";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useTranslation } from "react-i18next";
import type { TermState } from "store/managementStore";

import ProvisionDetails from "./ProvisionDetails";

const GeneralInformation = ({ stats, statsStatus }: Partial<TermState>) => {
    const [t] = useTranslation();
    const { countryFeatures } = useCompanySettings();
    const { netBlocks } = countryFeatures.statementBlocks;
    const showGrossDuty = netBlocks.length > 0;
    const showVat = netBlocks.includes("vat");
    const showWithholding = netBlocks.includes("withholding");
    const showContributions = netBlocks.includes("precount") || netBlocks.includes("ircec");

    return (
        <div className="management-dashboard">
            <div className="managment-info-list">
                <div className="list-item row-container">
                    <Avatar style={{ backgroundColor: "#F2BFCD40", color: "black" }}>
                        <Titles />
                    </Avatar>
                    <div className="counted-info">
                        <span className="counter-name">{t("Management.Titles")}</span>
                        {statsStatus === "pending" || !stats ? (
                            <Skeleton variant="text" width="50%" />
                        ) : (
                            <span className="counter">{formatNumber(stats.titlesCount)}</span>
                        )}
                    </div>
                </div>
                <div className="list-item row-container">
                    <Avatar style={{ backgroundColor: "#F4FDA994", color: "black" }}>
                        <Beneficiaries />
                    </Avatar>
                    <div className="counted-info">
                        <span className="counter-name">{t("Management.Beneficiaries")}</span>
                        {statsStatus === "pending" || !stats ? (
                            <Skeleton variant="text" width="50%" />
                        ) : (
                            <span className="counter">{formatNumber(stats.beneficiarsCount)}</span>
                        )}
                    </div>
                </div>
                {showGrossDuty && (
                    <div className="list-item-wrapper">
                        <div className="list-item column-container">
                            <span className="counter-name">{t("Management.GrossDutiesExcludeHT")}</span>
                            {statsStatus === "pending" || !stats ? (
                                <Skeleton variant="text" width="50%" />
                            ) : (
                                <div className="counter flex">
                                    {formatNumber(stats.grossDuty / 100, {
                                        isCurrency: true,
                                        noDigit: true,
                                    })}
                                </div>
                            )}
                        </div>
                        {statsStatus === "succeeded" && stats && (
                            <ProvisionDetails
                                initialProvision={stats.initialProvision}
                                currentProvision={stats.currentProvision}
                            />
                        )}
                    </div>
                )}
                <div className="list-item column-container net">
                    <span className="counter-name">
                        {showGrossDuty ? t("Management.NetToPay") : t("Management.Rights")}
                    </span>
                    {statsStatus === "pending" || !stats ? (
                        <Skeleton variant="text" width="50%" />
                    ) : (
                        <div className="counter flex">
                            {formatNumber(stats.netToPay / 100, {
                                isCurrency: true,
                                noDigit: true,
                            })}
                            <Tooltip title={t("Management.LeftToPayTooltip")} arrow>
                                <span className="small-text">
                                    {t("Management.LeftToPay", {
                                        value: formatNumber(stats.netRemainingToPay / 100, {
                                            isCurrency: true,
                                            noDigit: true,
                                        }),
                                    })}
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {showVat && (
                    <div className="list-item column-container">
                        <span className="counter-name">{t("Management.TVA")}</span>
                        {statsStatus === "pending" || !stats ? (
                            <Skeleton variant="text" width="50%" />
                        ) : (
                            <span className="counter">
                                {formatNumber(stats.vat / 100, {
                                    isCurrency: true,
                                    noDigit: true,
                                })}
                            </span>
                        )}
                    </div>
                )}
                {showWithholding && (
                    <div className="list-item column-container">
                        <span className="counter-name">{t("Management.WithheldVAT")}</span>
                        {statsStatus === "pending" || !stats ? (
                            <Skeleton variant="text" width="50%" />
                        ) : (
                            <span className="counter">
                                {formatNumber(stats.withheldVat / 100, {
                                    isCurrency: true,
                                    noDigit: true,
                                })}
                            </span>
                        )}
                    </div>
                )}
                {showContributions && (
                    <div className="list-item column-container">
                        <span className="counter-name">{t("Management.Contributions")}</span>
                        {statsStatus === "pending" || !stats ? (
                            <Skeleton variant="text" width="50%" />
                        ) : (
                            <span className="counter">
                                {formatNumber(stats.contributions / 100, {
                                    isCurrency: true,
                                    noDigit: true,
                                })}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GeneralInformation;
